/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ForgePioGithubIcon } from '../../../../../common/constants/3p-product-configs/forge-pio-github-prod/icon';
import { onBannerDismissed } from '../../../../../common/utils/events/banner-dismissed';
import { useLogException } from '../../../../../controllers/error-boundary/utils';
import { useQuickFindAnalytics } from '../../../../../controllers/quick-find/analytics';
import { usePrimaryProduct, useUser } from '../../../../../controllers/store/bootstrap';

import { detectJiraGninIntent } from './jira-gnin-intent';
import { detectGithubTipKeyword } from './keywords';
import { messages } from './messages';
import { dismissGithubTipForKeyword, shouldShowGithubTipForKeyword } from './storage';

const styles = cssMap({
	banner: {
		display: 'flex',
		width: '100%',
		alignItems: 'center',
		boxSizing: 'border-box',
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		borderRadius: token('radius.large'),
		color: token('color.text'),
	},
	iconWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		// 24px matches the result/recents rows' icon area (`size.200`) and the
		// 24x24 GitHub SVG. `width`/`height` in cssMap take literal dimensions,
		// not space tokens.
		width: '24px',
		height: '24px',
	},
	text: {
		flex: '1 1 auto',
		marginLeft: token('space.150'),
		marginRight: token('space.150'),
	},
});

const linkOverrideStyles = css({
	appearance: 'none',
	padding: 0,
	margin: 0,
	border: 'none',
	background: 'none',
	font: 'inherit',
	cursor: 'pointer',
	textDecoration: 'none',
	color: token('color.link'),
	borderRadius: token('radius.small'),
	'&:hover': {
		textDecoration: 'none',
		color: token('color.link'),
	},
	'&:focus': {
		textDecoration: 'none',
		color: token('color.link'),
	},
	'&:focus-visible': {
		outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
		outlineOffset: token('space.025'),
	},
	'&:active': {
		textDecoration: 'none',
		color: token('color.link.pressed'),
	},
});

export type GithubTipBannerProps = {
	query: string;
	onConnect?: () => void;
	/** When `true`, only literal GitHub keywords trigger the banner (Test 2 cohort). */
	disableGninTrigger?: boolean;
};

export const GithubTipBanner = ({
	query,
	onConnect,
	disableGninTrigger = false,
}: GithubTipBannerProps): React.JSX.Element | null => {
	const [user] = useUser();
	const userId = user?.id ?? undefined;
	const [primaryProduct] = usePrimaryProduct();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const logException = useLogException();

	// Triggers (in precedence order): literal GitHub keyword, then Jira
	// GNIN intent (typed JQL / work-item key). Both paths feed the same
	// `matchedKeyword` into storage.
	const { githubHit, gninHit } = useMemo(
		() => ({
			githubHit: detectGithubTipKeyword(query),
			gninHit: disableGninTrigger ? null : detectJiraGninIntent(query, primaryProduct),
		}),
		[query, primaryProduct, disableGninTrigger],
	);
	const matchedKeyword = githubHit ?? gninHit;

	const [sessionDismissedKeyword, setSessionDismissedKeyword] = useState<string | null>(null);

	const [isAllowedByStorage, setIsAllowedByStorage] = useState(false);

	useEffect(() => {
		if (matchedKeyword === null) {
			setIsAllowedByStorage(false);
			return;
		}
		setIsAllowedByStorage(shouldShowGithubTipForKeyword(matchedKeyword, userId));
	}, [matchedKeyword, userId]);

	const isVisible =
		matchedKeyword !== null && sessionDismissedKeyword !== matchedKeyword && isAllowedByStorage;

	const handleConnect = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			event.stopPropagation();
			onConnect?.();
		},
		[onConnect],
	);

	const handleDismiss = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			event.stopPropagation();
			if (matchedKeyword) {
				dismissGithubTipForKeyword(matchedKeyword, userId, logException);
				// When a query matches both triggers, suppress both so the
				// banner stays dismissed regardless of which path activates next.
				if (githubHit && gninHit) {
					dismissGithubTipForKeyword(gninHit, userId, logException);
				}
				setSessionDismissedKeyword(matchedKeyword);
			}
			fireAnalyticsEvent(
				onBannerDismissed({
					isOnSearchDialog: true,
					componentName: 'GithubTipBanner',
				}),
			);
		},
		[matchedKeyword, userId, fireAnalyticsEvent, logException, githubHit, gninHit],
	);

	if (!isVisible) {
		return null;
	}

	return (
		<Box xcss={styles.banner} testId="github-tip-banner">
			<Box xcss={styles.iconWrapper}>
				<ForgePioGithubIcon />
			</Box>
			<Box xcss={styles.text}>
				<Inline space="space.050" alignBlock="baseline">
					<FormattedMessage
						{...messages.tipBanner}
						values={{
							b: (chunks) => <Text weight="bold">{chunks}</Text>,
							connectLink: (chunks) => (
								<button
									type="button"
									css={linkOverrideStyles}
									onClick={handleConnect}
									data-testid="github-tip-banner-connect-link"
								>
									{chunks}
								</button>
							),
						}}
					/>
				</Inline>
			</Box>
			<IconButton
				icon={CrossIcon}
				spacing="compact"
				onClick={handleDismiss}
				label={<FormattedMessage {...messages.dismissBannerLabel} />}
				appearance="subtle"
				testId="github-tip-banner-dismiss"
			/>
		</Box>
	);
};
